import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Upload, Trash2, Pencil, UserCog } from "lucide-react";

import { ColorSwatchGrid, DEFAULT_PALETTE } from "@/components/ColorSwatchGrid";

const PALETTE = DEFAULT_PALETTE;
const DAY_KEYS = ["0", "1", "2", "3", "4", "5", "6"];
const DAY_LABELS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

const emptyHours = () => Object.fromEntries(
  DAY_KEYS.map((k) => [k, k === "6"
    ? { closed: true, open: "10:00", close: "13:00", open2: "", close2: "" }
    : { closed: false, open: "09:00", close: "13:00", open2: "15:00", close2: "19:00" }
  ])
);

export default function Staff({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  function emptyForm() {
    return { name: "", email: "", phone: "", role_title: "Parrucchiere", color: PALETTE[0], avatar_url: null, working_hours: emptyHours(), service_ids: [] };
  }

  const load = async () => {
    const [st, sv] = await Promise.all([api.get("/staff"), api.get("/services").catch(() => ({ data: [] }))]);
    setItems(st.data);
    setServices(sv.data);
  };
  useEffect(() => { load(); }, []);

  const openForm = (s = null) => {
    if (s) {
      setEditing(s.id);
      setForm({
        name: s.name, email: s.email || "", phone: s.phone || "",
        role_title: s.role_title || "Parrucchiere", color: s.color || PALETTE[0],
        avatar_url: s.avatar_url || null,
        working_hours: s.working_hours || emptyHours(),
        service_ids: s.service_ids || [],
      });
    } else {
      setEditing(null);
      setForm({ ...emptyForm(), color: PALETTE[Math.floor(Math.random() * PALETTE.length)] });
    }
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, email: form.email || null, phone: form.phone || null };
      if (editing) await api.patch(`/staff/${editing}`, payload);
      else await api.post("/staff", payload);
      toast.success(editing ? "Aggiornato" : "Aggiunto al team");
      setShowForm(false); load();
    } catch { toast.error("Errore"); }
  };
  const remove = async (id) => { if (!window.confirm("Rimuovere questo membro?")) return; await api.delete(`/staff/${id}`); load(); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, avatar_url: data.path }));
      toast.success("Foto caricata");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload fallito"); }
    finally { setUploading(false); }
  };

  const setDay = (day, patch) => setForm((f) => ({ ...f, working_hours: { ...f.working_hours, [day]: { ...f.working_hours[day], ...patch } } }));

  const token = localStorage.getItem("sp_token");
  const avatarSrc = (path) => path ? `${API}/files/${path}${token ? `?auth=${token}` : ""}` : null;

  return (
    <div data-testid="staff-page" className={embedded ? "space-y-6" : "space-y-8"}>
      <header className="flex items-end justify-between">
        <div>
          <h1 className={`font-display font-black tracking-tight inline-flex items-center gap-3 ${embedded ? "text-xl" : "text-4xl"}`}>
            <UserCog className={`${embedded ? "h-5 w-5" : "h-7 w-7"} text-[color:var(--sp-brand)]`} />
            {embedded ? "Collaboratori" : "Staff"}
          </h1>
        </div>
        <button data-testid="staff-new-btn" onClick={() => openForm()} className="sp-btn-primary inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Nuovo membro</button>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="col-span-full sp-card p-12 text-center text-[color:var(--sp-text-secondary)]">Nessun membro dello staff.</div>}
        {items.map((s) => (
          <div key={s.id} data-testid={`staff-card-${s.id}`} className="sp-card sp-card-hover p-6 relative">
            <div className="absolute top-3 right-3 flex gap-1">
              <button data-testid={`staff-edit-${s.id}`} onClick={() => openForm(s)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-brand)]"><Pencil className="h-3.5 w-3.5" /></button>
              <button data-testid={`staff-delete-${s.id}`} onClick={() => remove(s.id)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-danger)]"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex items-center gap-4">
              {s.avatar_url ? (
                <img src={avatarSrc(s.avatar_url)} alt={s.name} className="w-14 h-14 rounded-full object-cover" style={{ boxShadow: `0 0 0 3px ${s.color || "#3B82F6"}22` }} />
              ) : (
                <div className="w-14 h-14 rounded-full text-white flex items-center justify-center font-display font-black text-lg" style={{ background: s.color || "#3B82F6" }}>{s.name.charAt(0).toUpperCase()}</div>
              )}
              <div>
                <button onClick={() => openForm(s)} className="font-display font-black text-lg hover:text-[color:var(--sp-brand)] text-left">{s.name}</button>
                <div className="text-xs text-[color:var(--sp-text-secondary)]">{s.role_title}</div>
              </div>
            </div>
            {(s.email || s.phone) && (
              <div className="mt-4 text-xs text-[color:var(--sp-text-secondary)] space-y-1">
                {s.email && <div>{s.email}</div>}{s.phone && <div>{s.phone}</div>}
              </div>
            )}
            {s.service_ids && s.service_ids.length > 0 && (
              <div className="mt-4 pt-3 border-t border-[color:var(--sp-border)]">
                <div className="text-[10px] font-bold text-[color:var(--sp-text-muted)] uppercase tracking-wider mb-1.5">Servizi ({s.service_ids.length})</div>
                <div className="flex flex-wrap gap-1" data-testid={`staff-services-badges-${s.id}`}>
                  {s.service_ids.slice(0, 4).map((sid) => {
                    const svc = services.find((x) => x.id === sid);
                    if (!svc) return null;
                    return (
                      <span key={sid} className="text-[10px] px-2 py-0.5 rounded-full text-white font-bold" style={{ background: svc.color || "#06B6D4" }}>
                        {svc.name}
                      </span>
                    );
                  })}
                  {s.service_ids.length > 4 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--sp-subtle)] text-[color:var(--sp-text-muted)] font-bold">
                      +{s.service_ids.length - 4}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="sp-card w-full max-w-xl p-8 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-black">{editing ? "Modifica membro" : "Nuovo membro"}</h3>
              <button onClick={() => setShowForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="flex items-center gap-4">
                {form.avatar_url ? (
                  <img src={avatarSrc(form.avatar_url)} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-display font-black text-xl" style={{ background: form.color }}>{form.name.charAt(0).toUpperCase() || "?"}</div>
                )}
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={upload} />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="sp-btn-ghost text-sm inline-flex items-center gap-2">
                    <Upload className="h-4 w-4" />{uploading ? "Caricamento…" : "Foto profilo"}
                  </button>
                  {form.avatar_url && (
                    <button type="button" onClick={() => setForm({ ...form, avatar_url: null })} className="sp-btn-ghost text-sm inline-flex items-center gap-2 !text-[color:var(--sp-danger)]">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div><label className="text-sm font-bold">Nome</label><input data-testid="staff-name-input" required placeholder="Nome cognome" className="sp-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="text-sm font-bold">Ruolo</label><input data-testid="staff-role-input" placeholder="es. Parrucchiere, Colorista" className="sp-input mt-1" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-bold">Email</label><input data-testid="staff-email-input" type="email" placeholder="email@..." className="sp-input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><label className="text-sm font-bold">Telefono</label><input data-testid="staff-phone-input" placeholder="+39 ..." className="sp-input mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>

              <div>
                <label className="text-sm font-bold">Colore identificativo</label>
                <ColorSwatchGrid
                  value={form.color}
                  onChange={(c) => setForm({ ...form, color: c })}
                  testIdPrefix="staff-color"
                  className="mt-2"
                />
              </div>

              <div>
                <label className="text-sm font-bold block">Servizi che può eseguire</label>
                <p className="text-[11px] text-[color:var(--sp-text-muted)] mb-2">
                  Se lasciato vuoto, il collaboratore può eseguire tutti i servizi. Altrimenti in agenda verrà mostrato solo per i servizi selezionati.
                </p>
                {services.length === 0 ? (
                  <div className="text-xs text-[color:var(--sp-text-muted)] italic">Nessun servizio configurato. Crea prima i tuoi servizi.</div>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="staff-services-picker">
                    {services.map((s) => {
                      const active = form.service_ids.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`staff-service-chip-${s.id}`}
                          onClick={() => setForm((f) => ({
                            ...f,
                            service_ids: active
                              ? f.service_ids.filter((x) => x !== s.id)
                              : [...f.service_ids, s.id],
                          }))}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            active
                              ? "border-transparent text-white"
                              : "border-[color:var(--sp-border)] text-[color:var(--sp-text-secondary)] hover:border-[color:var(--sp-brand)]"
                          }`}
                          style={active ? { background: s.color || "#06B6D4" } : undefined}
                        >
                          {s.name}
                        </button>
                      );
                    })}
                    {form.service_ids.length > 0 && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, service_ids: [] }))}
                        data-testid="staff-services-clear"
                        className="text-[11px] px-3 py-1.5 rounded-full text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]">
                        Svuota (può fare tutto)
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-bold block mb-2">Orari di lavoro</label>
                <p className="text-xs text-[color:var(--sp-text-muted)] mb-3">Puoi impostare due fasce (mattino e pomeriggio) per gestire la pausa pranzo.</p>
                <div className="space-y-3">
                  {DAY_KEYS.map((k, i) => {
                    const wh = form.working_hours[k] || { closed: true };
                    return (
                      <div key={k} className="p-3 rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/40">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-sm">{DAY_LABELS[i]}</span>
                          <label className="text-xs flex items-center gap-2">
                            <input type="checkbox" checked={!wh.closed} onChange={(e) => setDay(k, { closed: !e.target.checked })} />
                            {wh.closed ? "Non lavora" : "Lavora"}
                          </label>
                        </div>
                        {!wh.closed && (
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <div className="text-[10px] font-bold text-[color:var(--sp-text-muted)] uppercase tracking-wider mb-1">Mattino</div>
                              <div className="flex items-center gap-1">
                                <input type="time" className="sp-input !py-1 flex-1 text-xs" value={wh.open || ""} onChange={(e) => setDay(k, { open: e.target.value })} />
                                <span>→</span>
                                <input type="time" className="sp-input !py-1 flex-1 text-xs" value={wh.close || ""} onChange={(e) => setDay(k, { close: e.target.value })} />
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] font-bold text-[color:var(--sp-text-muted)] uppercase tracking-wider mb-1">Pomeriggio</div>
                              <div className="flex items-center gap-1">
                                <input type="time" placeholder="—" className="sp-input !py-1 flex-1 text-xs" value={wh.open2 || ""} onChange={(e) => setDay(k, { open2: e.target.value })} />
                                <span>→</span>
                                <input type="time" placeholder="—" className="sp-input !py-1 flex-1 text-xs" value={wh.close2 || ""} onChange={(e) => setDay(k, { close2: e.target.value })} />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <button data-testid="staff-submit-btn" className="sp-btn-primary w-full">{editing ? "Salva modifiche" : "Aggiungi al team"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
